import { ShieldCheck, User } from 'lucide-react';
import { useTranslation } from '@aimsos/i18n';
import { usePersona } from '../hooks';

/**
 * Demo persona toggle bar (CT-4203). A MOCK affordance — stands in for real RBAC
 * (CT-3900) — that flips the whole Admin Studio integrations surface between the
 * admin experience and the end-user experience. Mirrors the prototype's "Demo"
 * persona pill: in V1 there is no role, so a tester switches personas here to
 * preview "My requests" (Requests) and the read-only end-user views (Integrations,
 * provider/instance). Removed when RBAC lands — the role then comes from auth.
 *
 * Persona is global (module store) so every `useIntegrationsPermissions` consumer
 * reacts at once; this bar is the single control.
 */
const PersonaDemoBar = () => {
  const { t } = useTranslation('admin-studio');
  const { persona, setPersona } = usePersona();

  const options = [
    { value: 'admin', label: t('integrations.persona.admin'), Icon: ShieldCheck },
    { value: 'user', label: t('integrations.persona.user'), Icon: User },
  ];

  return (
    <div className="flex shrink-0 items-center justify-end gap-2 border-b border-content-muted bg-surface-base px-6 py-1.5">
      <span
        className="rounded bg-surface-muted px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] text-content-faint"
        title={t('integrations.persona.hint')}
      >
        {t('integrations.persona.demo')}
      </span>
      <span className="hidden text-[11px] font-medium text-content-faint sm:inline">
        {t('integrations.persona.label')}
      </span>
      <div
        role="tablist"
        aria-label={t('integrations.persona.label')}
        className="inline-flex items-center rounded-full border border-content-muted bg-surface-muted p-0.5"
      >
        {options.map(({ value, label, Icon }) => {
          const active = persona === value;
          return (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setPersona(value)}
              className={[
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors duration-150',
                active
                  ? 'bg-surface-elevated text-content shadow-sm'
                  : 'text-content-secondary hover:text-content',
              ].join(' ')}
            >
              <Icon size={13} strokeWidth={1.8} aria-hidden />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PersonaDemoBar;
